import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck, Plus, X, Loader2, User, Phone, MapPin, Package,
  CheckCircle, Clock, AlertCircle, Search, ChevronDown,
  Bike, Car, Navigation, Send, Trash2, Edit2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  assigned: 'bg-blue-100 text-blue-700 border-blue-200',
  picked_up: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  delivered: 'bg-green-100 text-green-700 border-green-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
};

const VEHICLE_ICONS = { motorcycle: Bike, car: Car, bicycle: Bike, van: Truck };

const DeliveryPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('deliveries');
  const [stats, setStats] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateDelivery, setShowCreateDelivery] = useState(false);
  const [showCreateDriver, setShowCreateDriver] = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({ customer_name: '', customer_phone: '', delivery_address: '', delivery_city: '', delivery_notes: '', delivery_fee: 0, total: 0, payment_method: 'cash' });
  const [driverForm, setDriverForm] = useState({ name: '', phone: '', vehicle_type: 'motorcycle', notes: '' });

  useEffect(() => { init(); }, []);

  const init = async () => {
    setLoading(true);
    try {
      const res = await api.get('/shops');
      if (res.data.length > 0) {
        setShop(res.data[0]);
        await fetchAll(res.data[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAll = async (sid) => {
    const [statsRes, delRes, drvRes] = await Promise.all([
      api.get(`/shops/${sid}/delivery-stats`),
      api.get(`/shops/${sid}/deliveries?limit=50${statusFilter ? `&status=${statusFilter}` : ''}`),
      api.get(`/shops/${sid}/drivers`),
    ]);
    setStats(statsRes.data);
    setDeliveries(delRes.data.deliveries);
    setDrivers(drvRes.data);
  };

  const refresh = () => shop && fetchAll(shop.id);

  useEffect(() => { if (shop) fetchAll(shop.id); }, [statusFilter]);

  const handleCreateDelivery = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/shops/${shop.id}/deliveries`, form);
      setShowCreateDelivery(false);
      setForm({ customer_name: '', customer_phone: '', delivery_address: '', delivery_city: '', delivery_notes: '', delivery_fee: 0, total: 0, payment_method: 'cash' });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/shops/${shop.id}/drivers`, driverForm);
      setShowCreateDriver(false);
      setDriverForm({ name: '', phone: '', vehicle_type: 'motorcycle', notes: '' });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleAssign = async (deliveryId, driverId) => {
    try {
      await api.put(`/shops/${shop.id}/deliveries/${deliveryId}/assign?driver_id=${driverId}`);
      setShowAssign(null);
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleStatusUpdate = async (deliveryId, status) => {
    try {
      await api.put(`/shops/${shop.id}/deliveries/${deliveryId}/status`, { status });
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleDeleteDriver = async (driverId) => {
    if (!window.confirm(t('dlv_delete_driver_confirm'))) return;
    try {
      await api.delete(`/shops/${shop.id}/drivers/${driverId}`);
      refresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  if (loading) return <ShopLayout><div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" /></div></ShopLayout>;

  return (
    <ShopLayout>
      <div className="space-y-5" data-testid="delivery-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#1e3a5f]">{t('dlv_title')}</h2>
            <p className="text-slate-500 text-sm">{t('dlv_subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCreateDriver(true)} className="vf-btn-secondary flex items-center gap-2" data-testid="add-driver-btn">
              <User className="w-4 h-4" /> {t('dlv_add_driver')}
            </button>
            <button onClick={() => setShowCreateDelivery(true)} className="vf-btn-primary flex items-center gap-2" data-testid="create-delivery-btn">
              <Plus className="w-4 h-4" /> {t('dlv_new_delivery')}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {[
              { label: t('dlv_pending'), val: stats.pending, color: 'text-yellow-600' },
              { label: t('dlv_in_progress'), val: stats.in_progress, color: 'text-blue-600' },
              { label: t('dlv_delivered'), val: stats.delivered, color: 'text-green-600' },
              { label: t('dlv_failed'), val: stats.failed, color: 'text-red-600' },
              { label: t('dlv_drivers_available'), val: `${stats.drivers_available}/${stats.drivers_total}`, color: 'text-[#1e3a5f]' },
              { label: t('dlv_revenue'), val: `${stats.revenue.toFixed(2)}`, color: 'text-[#22c55e]' },
            ].map((s, i) => (
              <div key={i} className="bg-white border-2 border-slate-100 p-3 text-center">
                <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                <p className="text-xs text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b-2 border-slate-100">
          {['deliveries', 'drivers'].map(t_tab => (
            <button key={t_tab} onClick={() => setTab(t_tab)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-[2px] transition-colors ${tab === t_tab ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
              data-testid={`tab-${t_tab}`}>
              {t_tab === 'deliveries' ? t('dlv_orders') : t('dlv_drivers')}
            </button>
          ))}
        </div>

        {/* Deliveries Tab */}
        {tab === 'deliveries' && (
          <div>
            <div className="flex gap-2 mb-4">
              {['', 'pending', 'assigned', 'picked_up', 'delivered', 'failed'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 text-xs rounded-sm border ${statusFilter === s ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                  data-testid={`filter-${s || 'all'}`}>
                  {s ? s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : t('dlv_all')}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {deliveries.map((d) => (
                <motion.div key={d.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border-2 border-slate-100 p-4" data-testid={`delivery-${d.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#1e3a5f] font-mono text-sm">{d.delivery_number}</span>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-sm border ${STATUS_COLORS[d.status]}`}>
                          {d.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                        <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {d.customer_name}</span>
                        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {d.customer_phone}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                        <MapPin className="w-3.5 h-3.5" /> {d.delivery_address} {d.delivery_city && `, ${d.delivery_city}`}
                      </div>
                      {d.driver_name && <p className="text-xs text-blue-600 mt-1 flex items-center gap-1"><Truck className="w-3 h-3" /> {d.driver_name}</p>}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#22c55e]">{d.total?.toFixed(2)} EUR</p>
                      {d.delivery_fee > 0 && <p className="text-xs text-slate-400">+{d.delivery_fee?.toFixed(2)} fee</p>}
                      <p className="text-xs text-slate-400 mt-1">{new Date(d.created_at).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</p>
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                    {d.status === 'pending' && (
                      <button onClick={() => setShowAssign(d.id)} className="vf-btn-primary text-xs flex items-center gap-1" data-testid={`assign-${d.id}`}>
                        <Send className="w-3 h-3" /> {t('dlv_assign')}
                      </button>
                    )}
                    {d.status === 'assigned' && (
                      <button onClick={() => handleStatusUpdate(d.id, 'picked_up')} className="bg-indigo-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-indigo-700 flex items-center gap-1">
                        <Package className="w-3 h-3" /> {t('dlv_picked_up')}
                      </button>
                    )}
                    {(d.status === 'picked_up' || d.status === 'assigned') && (
                      <>
                        <button onClick={() => handleStatusUpdate(d.id, 'delivered')} className="bg-green-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-green-700 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> {t('dlv_mark_delivered')}
                        </button>
                        <button onClick={() => handleStatusUpdate(d.id, 'failed')} className="bg-red-600 text-white px-3 py-1.5 text-xs font-medium hover:bg-red-700 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {t('dlv_mark_failed')}
                        </button>
                      </>
                    )}
                    {d.status === 'pending' && (
                      <button onClick={() => handleStatusUpdate(d.id, 'cancelled')} className="text-slate-400 px-3 py-1.5 text-xs hover:text-red-500">
                        {t('dlv_cancel')}
                      </button>
                    )}
                  </div>

                  {/* Assign Modal Inline */}
                  {showAssign === d.id && (
                    <div className="mt-3 pt-3 border-t border-blue-200 bg-blue-50 p-3 -mx-4 -mb-4">
                      <p className="text-sm font-semibold text-[#1e3a5f] mb-2">{t('dlv_select_driver')}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {drivers.filter(dr => dr.status === 'available').map(dr => (
                          <button key={dr.id} onClick={() => handleAssign(d.id, dr.id)}
                            className="bg-white border border-slate-200 p-2 text-left hover:border-[#22c55e] transition-colors rounded-sm"
                            data-testid={`assign-driver-${dr.id}`}>
                            <p className="font-medium text-[#1e3a5f] text-sm">{dr.name}</p>
                            <p className="text-xs text-slate-400">{dr.phone}</p>
                          </button>
                        ))}
                        {drivers.filter(dr => dr.status === 'available').length === 0 && (
                          <p className="text-sm text-slate-500 col-span-3">{t('dlv_no_available_drivers')}</p>
                        )}
                      </div>
                      <button onClick={() => setShowAssign(null)} className="text-xs text-slate-500 mt-2 hover:text-slate-700">{t('dlv_cancel')}</button>
                    </div>
                  )}
                </motion.div>
              ))}
              {deliveries.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Truck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>{t('dlv_no_deliveries')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Drivers Tab */}
        {tab === 'drivers' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {drivers.map(dr => {
              const VIcon = VEHICLE_ICONS[dr.vehicle_type] || Truck;
              return (
                <motion.div key={dr.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white border-2 border-slate-100 p-4" data-testid={`driver-card-${dr.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${dr.status === 'available' ? 'bg-green-100' : 'bg-amber-100'}`}>
                        <VIcon className={`w-4 h-4 ${dr.status === 'available' ? 'text-green-600' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1e3a5f]">{dr.name}</p>
                        <p className="text-xs text-slate-400">{dr.phone}</p>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteDriver(dr.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-driver-${dr.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${dr.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {dr.status === 'available' ? t('dlv_available') : t('dlv_busy')}
                    </span>
                    <span className="text-slate-400 text-xs">{dr.total_deliveries || 0} {t('dlv_completed')}</span>
                  </div>
                </motion.div>
              );
            })}
            {drivers.length === 0 && (
              <div className="col-span-3 text-center py-12 text-slate-400">
                <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>{t('dlv_no_drivers')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Delivery Modal */}
      <AnimatePresence>
        {showCreateDelivery && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-delivery-modal">
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h3 className="font-bold text-[#1e3a5f]">{t('dlv_new_delivery')}</h3>
                <button onClick={() => setShowCreateDelivery(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <form onSubmit={handleCreateDelivery} className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_customer_name')} *</label>
                    <input type="text" required value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="dlv-customer-name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_customer_phone')} *</label>
                    <input type="text" required value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="dlv-customer-phone" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_address')} *</label>
                  <input type="text" required value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="dlv-address" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_city')}</label>
                    <input type="text" value={form.delivery_city} onChange={e => setForm({ ...form, delivery_city: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_payment')}</label>
                    <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none">
                      <option value="cash">{t('pos_cash')}</option>
                      <option value="card">{t('pos_card')}</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Total (EUR)</label>
                    <input type="number" step="0.01" value={form.total} onChange={e => setForm({ ...form, total: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="dlv-total" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_fee')}</label>
                    <input type="number" step="0.01" value={form.delivery_fee} onChange={e => setForm({ ...form, delivery_fee: parseFloat(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_notes')}</label>
                  <textarea value={form.delivery_notes} onChange={e => setForm({ ...form, delivery_notes: e.target.value })} rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none resize-none" />
                </div>
                <button type="submit" disabled={saving} className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-delivery">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} {t('dlv_create')}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Driver Modal */}
      <AnimatePresence>
        {showCreateDriver && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-white w-full max-w-md" data-testid="create-driver-modal">
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h3 className="font-bold text-[#1e3a5f]">{t('dlv_add_driver')}</h3>
                <button onClick={() => setShowCreateDriver(false)}><X className="w-5 h-5 text-slate-400" /></button>
              </div>
              <form onSubmit={handleCreateDriver} className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_driver_name')} *</label>
                  <input type="text" required value={driverForm.name} onChange={e => setDriverForm({ ...driverForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="driver-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_driver_phone')} *</label>
                  <input type="text" required value={driverForm.phone} onChange={e => setDriverForm({ ...driverForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="driver-phone" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">{t('dlv_vehicle')}</label>
                  <select value={driverForm.vehicle_type} onChange={e => setDriverForm({ ...driverForm, vehicle_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none" data-testid="driver-vehicle">
                    <option value="motorcycle">{t('dlv_motorcycle')}</option>
                    <option value="car">{t('dlv_car')}</option>
                    <option value="bicycle">{t('dlv_bicycle')}</option>
                    <option value="van">{t('dlv_van')}</option>
                  </select>
                </div>
                <button type="submit" disabled={saving} className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="submit-driver">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <User className="w-4 h-4" />} {t('dlv_add_driver')}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </ShopLayout>
  );
};

export default DeliveryPage;
